import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Outlet, Link, createRootRouteWithContext, useRouter, redirect } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { reportClientError } from "@/lib/client-error-reporting";
import { applyDensityFromStorage } from "@/lib/density";
import { installAuthBridge } from "@/lib/auth-bridge";
import { getLocale } from "@/lib/i18n";
import { CommandPalette } from "@/components/CommandPalette";
import { GlobalNotifications } from "@/components/GlobalNotifications";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { evaluateRoute, readSession } from "@/lib/route-auth";
import { installDemoSession } from "@/lib/demo-mode";
import { DemoBanner } from "@/components/DemoBanner";
import { SpecialtyProvider } from "@/hooks/use-specialty";
import { SpecialtySwitcher } from "@/components/specialty/SpecialtySwitcher";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  // eslint-disable-next-line no-console
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportClientError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  /**
   * Global route guard (v10 rev 5).
   *
   * Rev 4 had no beforeLoad at the root and none on any of the 94 routes, so
   * every clinical screen was reachable by typing its URL. This runs before
   * any child route renders.
   *
   * NOTE: this is UX enforcement only. The API's deny-by-default guard is the
   * actual security boundary; see apps/api/src/middleware/auth-guard.ts.
   */
  beforeLoad: ({ location }) => {
    // rev 12: demo mode seeds a synthetic session so the UI is reviewable
    // while the identity stack is unfinished. It mints NO token — the API
    // keeps denying, so this cannot become a server-side bypass.
    installDemoSession();
    const decision = evaluateRoute(location.pathname, readSession());
    if (!decision.allowed) {
      throw redirect({ to: decision.redirect });
    }
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "DIKDOC — Opérations de Santé" },
      {
        name: "description",
        content:
          "DIKDOC — Healthcare Operating System: tableau de bord clinique, agenda, patients, consultations, facturation CNSS/CNOPS/AMO, pharmacie, laboratoire, imagerie.",
      },
      { name: "author", content: "DIKDOC" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Cairo:wght@400;500;600;700&display=swap",
      },
    ],
  }),
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Phase 2 — the clinical context. Mounted once, at the root: the
          specialty scopes the whole interface through the data-specialty
          attribute and the CSS variables of the theme. */}
      <SpecialtyProvider>
        <ErrorBoundary route="/">
          <Outlet />
        </ErrorBoundary>
        {/* Phase 8 + 12 — Global shell components */}
        <CommandPalette />
        <GlobalNotifications />
        <DemoBanner />
        <div className="fixed bottom-4 right-4 z-50">
          <SpecialtySwitcher />
        </div>
      </SpecialtyProvider>
    </QueryClientProvider>
  );
}
